/** Configuration Studio → Communications (Release 1). */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { SenderIdentityCard } from "@/components/comms/SenderIdentityCard";
import { IssuerMailboxCard } from "@/components/comms/IssuerMailboxCard";
import { IssuerBrandingCard } from "@/components/comms/IssuerBrandingCard";
import { WebhookCard } from "@/components/comms/WebhookCard";
import { LetterheadCard } from "@/components/comms/LetterheadCard";
import { OutboxPreviewDialog } from "@/components/comms/OutboxPreviewDialog";
import { DeliveryLogPanel } from "@/components/comms/DeliveryLogPanel";
import { DesignGallery } from "@/components/comms/DesignGallery";
import { EmailPreviewFrame } from "@/components/comms/EmailPreviewFrame";
import CustomerResponsePanel from "@/components/comms/CustomerResponsePanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TABS = ["Overview", "Email Templates", "Customer Milestones", "Outbox & Review", "Design Review", "Branding", "Delivery Logs", "Customer Responses"];

export default function CommunicationsStudio() {
  const [tab, setTab] = useState("Overview");
  const [templates, setTemplates] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [logs, setLogs] = useState([]);
  const [branding, setBranding] = useState({});
  const [sender, setSender] = useState(null);
  const [eff, setEff] = useState(null);
  const [logoTs, setLogoTs] = useState(Date.now());
  const [edit, setEdit] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [tplPreview, setTplPreview] = useState(null);

  const loadAll = () => {
    api.get("/comms/templates").then(({ data }) => setTemplates(data.items)).catch(() => {});
    api.get("/comms/milestones").then(({ data }) => setMilestones(data.items)).catch(() => {});
    api.get("/comms/outbox").then(({ data }) => setOutbox(data.items)).catch(() => {});
    api.get("/comms/log").then(({ data }) => setLogs(data.items)).catch(() => {});
    api.get("/comms/branding/effective").then(({ data }) => setEff(data)).catch(() => setEff({ issuer: null }));
    api.get("/comms/branding").then(({ data }) => { setBranding(data.branding || {}); setSender(data.sender || null); }).catch(() => {});
  };
  useEffect(loadAll, []);

  const saveMilestone = async (m, patch) => {
    await api.put(`/comms/milestones/${m.milestone}`, { email: m.email, mode: m.mode, recipient_rule: m.recipient_rule, ...patch });
    toast.success("Milestone updated"); loadAll();
  };

  return (
    <div className="max-w-[1100px] mx-auto px-4 py-5" data-testid="comms-studio-page">
      <h1 className="text-lg font-semibold text-slate-900 mb-1">Communications</h1>
      <div className="text-[12px] text-slate-500 mb-3">Pilot mode: milestone emails are PREPARED FOR REVIEW by default — nothing auto-sends until you switch a milestone to Auto.</div>
      <div className="flex gap-1 flex-wrap mb-4">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`comms-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`text-[12px] rounded-full px-3 py-1 border ${tab === t ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{t}</button>
        ))}
        <span className="text-[10px] uppercase text-amber-700 border border-amber-300 rounded px-2 py-1 ml-auto">SMS / WhatsApp: SETUP REQUIRED</span>
      </div>

      {tab === "Customer Responses" && <CustomerResponsePanel />}
      {tab === "Overview" && eff && (eff.issuer?.sender || sender) && (() => { const iss = eff.issuer?.sender; const sd = iss ? { from_header: iss.from_header, reply_to: iss.mailbox, ready: iss.state === "CONNECTED", state: iss.state === "CONNECTED" ? iss.state : `${iss.state} — ${eff.issuer.display_name} mail is held until connected` } : sender; return (
        <div className="mb-3 text-[12px] text-slate-600 border border-slate-200 rounded-md px-3 py-2 bg-white flex flex-wrap gap-x-4 gap-y-1" data-testid="comms-overview-sender">
          <span>Sends as <span className="font-medium text-slate-900" data-testid="comms-overview-from">{sd.from_header}</span></span>
          <span>Reply-To <span className="font-medium text-slate-900">{sd.reply_to}</span></span>
          <span className={sd.ready ? "text-emerald-700" : "text-amber-700"} data-testid="comms-overview-sender-state">{sd.state === "DELIVERY_VERIFIED" ? "SENDER AUTHENTICATION VERIFIED (not delivery confirmation)" : sd.state.replace(/_/g, " ")}</span>
        </div>
      ); })()}
      {tab === "Overview" && (
        <div className="grid sm:grid-cols-4 gap-3">
          {[["Templates", templates.length], ["Awaiting review", outbox.filter((o) => o.status === "awaiting_review").length],
            ["Accepted by provider", logs.filter((l) => l.status === "SENT").length], ["Not sent", logs.filter((l) => l.status !== "SENT").length]].map(([l, v]) => (
            <div key={l} className="border border-slate-200 rounded-md px-4 py-3 bg-white">
              <div className="text-xl font-semibold text-slate-900">{v}</div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400">{l}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "Email Templates" && templates.map((t) => (
        <div key={t.id} className="border border-slate-200 rounded-md p-4 bg-white mb-3" data-testid={`comms-template-${t.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">{t.name}</span>
            <span className="text-[10px] text-slate-400">v{t.version} · {t.milestone}</span>
            <span className={`text-[10px] rounded px-1.5 py-0.5 ${t.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{t.active ? "ACTIVE" : "INACTIVE"}</span>
            <span className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEdit(edit?.id === t.id ? null : { ...t })} data-testid={`comms-edit-${t.id}`}>Edit</Button>
              <Button size="sm" variant="outline" onClick={() => api.get(`/comms/templates/${t.id}/preview`).then(({ data }) => setTplPreview(data)).catch((e) => toast.error(e.response?.data?.detail || "Preview failed"))} data-testid={`comms-preview-${t.id}`}>Preview (sample data)</Button>
              <Button size="sm" variant="outline" onClick={async () => { if (!window.confirm(`Send a REAL test email of "${t.name}" to your own address?`)) return; try { const { data } = await api.post(`/comms/templates/${t.id}/test-send`); data.ok ? toast.success(`Test sent to ${data.sent_to}`) : toast.error(data.error || "Test failed"); } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); } }} data-testid={`comms-test-${t.id}`}>Test send to me</Button>
            </span>
          </div>
          {edit?.id === t.id ? (
            <div className="mt-3 space-y-2">
              <Input value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} placeholder="Subject" data-testid="comms-subject-input" />
              <Input value={edit.preheader || ""} onChange={(e) => setEdit({ ...edit, preheader: e.target.value })} placeholder="Preview text (shown next to the subject in the inbox)" data-testid="comms-preheader-input" />
              <textarea rows={12} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })}
                className="w-full border border-slate-200 rounded-md p-2 text-[12px] font-mono" data-testid="comms-body-input" />
              <div className="text-[11px] text-slate-400">Variables: {"{{customer_name}} {{contact_name}} {{quote_number}} {{customer_po_number}} {{order_number}} {{order_date}} {{account_manager}} {{sender_signature}}"}</div>
              <div className="flex gap-2">
                <Button size="sm" onClick={async () => { await api.put(`/comms/templates/${t.id}`, { subject: edit.subject, body: edit.body, preheader: edit.preheader || "" }); toast.success("Saved — new version created"); setEdit(null); loadAll(); }} data-testid="comms-save-template">Save</Button>
                <Button size="sm" variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
              </div>
              {t.versions?.length > 0 && (
                <div className="text-[11px] text-slate-400">History: {t.versions.map((v) => `v${v.version} (${(v.saved_at || "").slice(0, 10)} ${v.saved_by || ""})`).join(" · ")}</div>
              )}
            </div>
          ) : (
            <pre className="mt-2 text-[11px] text-slate-600 whitespace-pre-wrap bg-slate-50 rounded p-2 max-h-40 overflow-y-auto">{t.subject}{t.preheader ? `\nPreview text: ${t.preheader}` : ""}{"\n\n"}{t.body}</pre>
          )}
        </div>
      ))}

      {tab === "Customer Milestones" && (
        <div className="border border-slate-200 rounded-md bg-white divide-y divide-slate-100">
          {milestones.map((m) => (
            <div key={m.milestone} className="px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid={`comms-milestone-${m.milestone}`}>
              <span className="text-[13px] font-medium w-44">{m.milestone.replace(/_/g, " ")}</span>
              <label className="flex items-center gap-1.5 text-[12px] text-slate-600">
                <Switch checked={m.email} onCheckedChange={(v) => saveMilestone(m, { email: v })} /> Email
              </label>
              <select value={m.mode} onChange={(e) => saveMilestone(m, { mode: e.target.value })}
                className="text-[12px] border border-slate-200 rounded px-2 py-1" data-testid={`comms-mode-${m.milestone}`}>
                <option value="review">Prepare for review</option>
                <option value="auto">Auto send</option>
              </select>
              <select value={m.recipient_rule} onChange={(e) => saveMilestone(m, { recipient_rule: e.target.value })}
                className="text-[12px] border border-slate-200 rounded px-2 py-1">
                {["primary_contact", "procurement_contact", "it_contact", "finance_contact", "delivery_contact", "account_manager"].map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
              </select>
              <span className="ml-auto text-[10px] text-slate-400">SMS/WhatsApp: setup required</span>
            </div>
          ))}
        </div>
      )}

      {tab === "Outbox & Review" && (outbox.length === 0
        ? <div className="text-sm text-slate-400 border border-dashed rounded-md p-6 text-center">No prepared communications yet. Mark a quote as Won, or move an order to Dispatched / Delivered / Invoiced, to prepare the milestone email here.</div>
        : outbox.map((o) => (
          <div key={o.id} className="border border-slate-200 rounded-md p-4 bg-white mb-3" data-testid={`comms-outbox-${o.id}`}>
            <div className="flex items-center gap-2 flex-wrap text-[12px]">
              <span className="font-medium text-slate-900">{o.milestone.replace(/_/g, " ")}</span>
              {o.order_code && <span className="rounded px-1.5 py-0.5 text-[10px] bg-sky-50 text-sky-700" data-testid={`comms-outbox-order-${o.id}`}>{o.order_code}</span>}
              <span className="text-slate-500">→ {o.to || "NO RECIPIENT"}</span>
              <span className={`rounded px-1.5 py-0.5 text-[10px] ${o.status === "awaiting_review" ? "bg-amber-50 text-amber-700" : o.status === "sent" ? "bg-emerald-50 text-emerald-700" : o.status === "failed" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-500"}`}>{o.status.replace(/_/g, " ")}</span>
              <span className="ml-auto text-slate-400">{(o.prepared_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
            {o.issues?.length > 0 && <div className="text-[11px] text-rose-600 mt-1">⚠ {o.issues.join(" · ")}</div>}
            <div className="text-[12px] text-slate-700 mt-1 font-medium">{o.subject}</div>
            <pre className="text-[11px] text-slate-600 whitespace-pre-wrap bg-slate-50 rounded p-2 mt-1 max-h-36 overflow-y-auto">{o.body}</pre>
            {(o.status === "awaiting_review" || o.status === "failed" || o.status === "retrying") && (
              <div className="flex gap-2 mt-2 items-center flex-wrap">
                {o.error && <span className="text-[11px] text-rose-600">Last attempt: {o.error}{o.action ? ` → ${o.action}` : ""}</span>}
                <Button variant="comms" size="sm" className="" data-testid={`comms-send-${o.id}`} onClick={() => setPreviewId(o.id)}>
                  {o.status === "failed" ? "Review & retry" : "Review & send"}
                </Button>
                <Button size="sm" variant="outline" data-testid={`comms-skip-${o.id}`} onClick={async () => { await api.post(`/comms/outbox/${o.id}/skip`); loadAll(); }}>Skip</Button>
              </div>
            )}
            {o.status === "sent" && <Button size="sm" variant="ghost" className="mt-1 h-6 text-[11px]" data-testid={`comms-view-${o.id}`} onClick={() => setPreviewId(o.id)}>View as sent</Button>}
          </div>
        )))}

      <OutboxPreviewDialog outboxId={previewId} onClose={() => setPreviewId(null)} onSent={loadAll} />
      <Dialog open={!!tplPreview} onOpenChange={(o) => !o && setTplPreview(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="template-preview-dialog">
          <DialogHeader><DialogTitle className="text-base">{tplPreview?.name} — preview with SAMPLE data</DialogTitle></DialogHeader>
          {tplPreview && (
            <div className="space-y-2 text-[12px]">
              <div className="border border-slate-200 rounded-md p-3 bg-slate-50/60 grid sm:grid-cols-2 gap-x-6 gap-y-1">
                <div className="sm:col-span-2"><span className="text-slate-500">Subject:</span> <span className="font-medium" data-testid="template-preview-subject">{tplPreview.subject}</span></div>
                <div><span className="text-slate-500">From:</span> {tplPreview.from}</div><div><span className="text-slate-500">Reply-To:</span> {tplPreview.reply_to}</div>
                {tplPreview.required?.length > 0 && <div className="sm:col-span-2 text-slate-500">Sending is blocked when missing: {tplPreview.required.join(", ")}</div>}
                {tplPreview.unresolved?.length > 0 && <div className="sm:col-span-2 text-rose-600">Unresolved variables in sample: {tplPreview.unresolved.join(", ")}</div>}
              </div>
              <EmailPreviewFrame html={tplPreview.html} text={tplPreview.text} testid="template-preview" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {tab === "Design Review" && <DesignGallery />}

      {tab === "Branding" && eff?.issuer && (
        <div className="max-w-xl space-y-3" data-testid="comms-branding-issuer">
          <IssuerBrandingCard issuer={eff.issuer} />
          <IssuerMailboxCard />
        </div>
      )}
      {tab === "Branding" && eff && !eff.issuer && (
        <div className="max-w-xl space-y-2">
          <SenderIdentityCard sender={sender} onChanged={loadAll} />
          <div className="mt-4"><IssuerMailboxCard /></div>
          <WebhookCard />
          <LetterheadCard />
          <div className="border border-slate-200 rounded-md p-3 bg-white" data-testid="comms-logo-section">
            <div className="text-[11px] text-slate-500 mb-2" data-testid="comms-logo-note">Single approved brand asset used in every email (header, replies' signature) with alt text. It does not create an inbox avatar or verified badge — that is BIMI, a separate task.</div>
            <div className="text-[11px] uppercase text-slate-400 mb-2">Company logo (shown at the top of every email)</div>
            {branding.logo_source && <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mb-2" data-testid="comms-logo-source">{branding.logo_source}{branding.logo_width ? ` · ${branding.logo_width}×${branding.logo_height}px (2x), displayed 220px desktop / 190px mobile / 150px in reply signatures` : ""}</div>}
            {branding.logo_mime ? (
              <div className="flex items-center gap-3">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/comms/branding/logo?ts=${logoTs}`}
                  alt="Company logo" className="h-10 max-w-[220px] object-contain border border-slate-100 rounded bg-white p-1"
                  data-testid="comms-logo-preview" />
                <Button size="sm" variant="outline" data-testid="comms-logo-remove"
                  onClick={async () => { await api.delete("/comms/branding/logo"); toast.success("Logo removed"); loadAll(); setLogoTs(Date.now()); }}>
                  Remove
                </Button>
              </div>
            ) : (
              <div className="text-[12px] text-slate-400 mb-2">No logo uploaded yet — emails show your company name as text.</div>
            )}
            <label className="inline-flex items-center gap-1.5 mt-2 text-[12px] font-medium text-emerald-700 border border-emerald-200 rounded px-2.5 py-1.5 cursor-pointer hover:bg-emerald-50">
              {branding.logo_mime ? "Replace logo" : "Upload logo"}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                data-testid="comms-logo-input"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (f.size > 512 * 1024) return toast.error("Logo must be under 512 KB");
                  const reader = new FileReader();
                  reader.onload = async () => {
                    try {
                      await api.post("/comms/branding/logo", { data_url: reader.result });
                      toast.success("Logo uploaded — it will appear on all outgoing emails");
                      loadAll(); setLogoTs(Date.now());
                    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
                  };
                  reader.readAsDataURL(f);
                  e.target.value = "";
                }} />
            </label>
            <div className="text-[10px] text-slate-400 mt-1.5">PNG / JPEG / WebP, max 512 KB. Recommended: transparent PNG, ~200×50 px.</div>
          </div>
          {["company_name", "phone", "email", "website", "legal_footer", "signature"].map((f) => (
            <div key={f}>
              <div className="text-[11px] uppercase text-slate-400">{f.replace(/_/g, " ")}</div>
              <Input value={branding[f] || ""} onChange={(e) => setBranding({ ...branding, [f]: e.target.value })} data-testid={`comms-brand-${f}`} />
            </div>
          ))}
          <Button size="sm" onClick={async () => { await api.put("/comms/branding", branding); toast.success("Branding saved"); }} data-testid="comms-brand-save">Save branding</Button>
        </div>
      )}

      {tab === "Delivery Logs" && <DeliveryLogPanel />}
    </div>
  );
}
